import tinycolor from 'tinycolor2';
export const BLACK = '#000000';
export const WHITE = '#FFFFFF';
export const invokeCssVariable = color => color && color.startsWith('--') ? `var(${color})` : color;

/**
 * The purpose of this method is to select either a dark or light contrasting
 * color. For example, given a background color, verify the text has
 * a high enough contrast to be legible.
 *
 * ! Important! Any style prop can be a color value or "var(--theme-*)".
 *
 * When calculating the contrast color:
 * - Color is a theme variable:
 *    - Return the associated theme contrast variable.
 *    - ComputedUserTheme.tsx is where theme contrast variables are defined.
 * - Color is a valid color (per tinycolor2):
 *    - Calculate and return the contrast color (as RGB).
 * - Color is invalid:
 *    - Return fallback theme contrast variable if provided.
 *    - Otherwise, return a dark contrasting color for historical reasons (black is the default)
 *
 * Lastly, this function has only been tested with HEX and RGB(A) colors. Other
 * color formats, like HSL, should work though.
 */

export const selectContrastingColor = (cssColor, options) => {
  const fallbackColor = invokeCssVariable(options?.fallbackCssVar);
  const lightContrastColor = invokeCssVariable(options?.lightContrastColor) ?? WHITE;
  const darkContrastColor = invokeCssVariable(options?.darkContrastColor) ?? BLACK;

  // * Assumes variable is a theme color, and the fallback variable exists.
  if (cssColor && fallbackColor && (cssColor.startsWith('var') || cssColor.startsWith('--'))) {
    return fallbackColor;
  }
  const color = tinycolor(cssColor);
  if (!color.isValid()) {
    if (fallbackColor) {
      return fallbackColor;
    }
    return darkContrastColor;
  }
  const {
    r,
    g,
    b
  } = color.toRgb();

  /*
   * Break up hex code into R G B intensity values
   * Determine the luminance value for RGB color
   * Return a dark or light contrast color depending on luminance value
   *
   * https://stackoverflow.com/a/41491220
   *
   * https://www.w3.org/TR/WCAG20/#relativeluminancedef
   */

  const contrastColorNormalized = [r / 255, g / 255, b / 255];
  const luminanceArr = contrastColorNormalized.map(contrastCol => {
    if (contrastCol <= 0.03928) {
      return contrastCol / 12.92;
    }
    return ((contrastCol + 0.055) / 1.055) ** 2.4;
  });
  const contrastColorLuminance = 0.2126 * luminanceArr[0] + 0.7152 * luminanceArr[1] + 0.0722 * luminanceArr[2];
  return contrastColorLuminance > 0.179 ? darkContrastColor : lightContrastColor;
};